/**
 * Formats raw device/controller UID bytes as an uppercase hexadecimal string.
 *
 * Invalid entries are filtered out. When the SDK returns a fixed-length buffer,
 * pass `uidSize` so only the leading logical bytes are used.
 */
export function formatDeviceUidHex(uid: unknown, uidSize?: number): string | undefined {
  if (!uid || !Array.isArray(uid) || uid.length === 0) return undefined;

  const slice = typeof uidSize === 'number' && uidSize >= 0 ? uid.slice(0, uidSize) : uid;

  const bytes = slice.filter((byte): byte is number => typeof byte === 'number' && Number.isInteger(byte) && byte >= 0 && byte <= 255);

  if (bytes.length === 0) return undefined;

  return bytes
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

/**
 * Normalizes SDK board revision data into the display label used in the UI.
 *
 * Board revisions are reported by firmware as uint8_t values in the range 0-255.
 * Returns null when missing or invalid so callers can distinguish unknown data from revision 0.
 */
export function formatBoardRevision(boardRevision: unknown): number | null {
  if (typeof boardRevision === 'number' && Number.isInteger(boardRevision) && boardRevision >= 0 && boardRevision <= 255) {
    return boardRevision;
  }

  console.warn('[deviceFormatters] Invalid board revision; expected uint8_t number 0-255.', boardRevision);
  return null;
}
